import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import React from 'react';
import {
  useCreateEpdProductPackagingMaterial,
  useDeleteProductPackagingMaterials,
  useUpdateEpdProductPackagingMaterial,
} from 'services/api/mutations';
import { useEPDDefinitionInformation, useEpdProductPackagingMaterials, useGetLCASpecification } from 'services/api/queries';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import { ProductPackagingMaterialModel } from 'types/content-declaration/ProductPackagingMaterialModel';
import { useContentDeclarationTerminologyHelper } from '../../hooks/useContentDeclarationTerminologyHelper';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import { IsISO21930StandardCompliant } from '../../utils';
import DynamicGridHeader from '../DynamicGridHeader';
import { cellNumberEditor, cellTextEditor } from '../GridCellEditors';
import {
  cellTotalTemplate,
  getBiogenicCarbonWeightHeader,
  getBiogenicCO2WeightHeader,
  numericCellBody,
  textCellBody,
} from '../GridService';
import { DataTableStyled } from '../StyledGrid';
import useDynamicGridWithEmptyFirstRow from '../useDynamicGridWithEmptyFirstRow';
import useGridRowSelectionViaCheckbox from '../useGridRowSelectionViaCheckbox';
import { YES_NO, YesNo } from '../../constants';

const PackagingMaterialsGrid: React.FunctionComponent<{
  epdVersionId: string;
  errors?: any;
  contentDeclarationType: EPDContentDeclarationProductType;
  packagingMaterialApplicableValue: YesNo | null;
}> = ({ epdVersionId, errors, contentDeclarationType, packagingMaterialApplicableValue }) => {
  const isReadOnly = useIsReadOnlyMode();
  const { replaceInString, replaceInNode, placeholders } = useContentDeclarationTerminologyHelper();

  const rows = useEpdProductPackagingMaterials(epdVersionId, contentDeclarationType).data;
  const createMutation = useCreateEpdProductPackagingMaterial(epdVersionId, contentDeclarationType);
  const updateMutation = useUpdateEpdProductPackagingMaterial(epdVersionId, contentDeclarationType);
  const deleteMutation = useDeleteProductPackagingMaterials(epdVersionId, contentDeclarationType);

  const { dataTableProps, checkboxColumnNode, handleDeleteSelected, isDeleteDisabled, gridId } =
    useGridRowSelectionViaCheckbox<ProductPackagingMaterialModel>(isReadOnly, deleteMutation);

  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;
  const isISO21930StandardSelected = IsISO21930StandardCompliant(epdDefinitionInformation);
  const lcaSpecification = useGetLCASpecification(epdVersionId!).data;

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  const { gridData, handleAddRow, handleCellEdit } = useDynamicGridWithEmptyFirstRow<ProductPackagingMaterialModel>({
    rows,
    createMutation,
    updateMutation,
    emptyEntity: {
      epdVersionId,
      declareContentType: contentDeclarationType,
      name: '',
      weight: null,
      weightPercent: null,
      biogenicCarbonWeight: null,
      biogenicCO2Weight: null,
    },
  });

  const isTableFilledIn = rows?.every((r) => r.weight) && rows?.every((r) => r.name);

  const header = (
    <DynamicGridHeader
      caption="Packaging materials"
      onAddRow={handleAddRow}
      onDeleteRows={handleDeleteSelected}
      deleteRowsDisabled={isDeleteDisabled}
      error={
        errors?.general ||
        (packagingMaterialApplicableValue === YES_NO.YES && !isTableFilledIn && 'Required') ||
        (errors && Object.keys(errors).length > 0 && 'Has errors')
      }
    />
  );

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column className="p-row-total" />
        <Column className="p-row-total" footer="Total" />
        <Column className="p-row-total" footer={cellTotalTemplate('weight', rows, errors)} />
        <Column className="p-row-total" footer={cellTotalTemplate('weightPercent', rows, errors)} />
        <Column className="p-row-total" footer={cellTotalTemplate('biogenicCarbonWeight', rows, errors)} />
        {isISO21930StandardSelected && (
          <Column className="p-row-total" footer={cellTotalTemplate('biogenicCO2Weight', rows, errors)} />
        )}
      </Row>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" />
        <Column className="p-row-note" footer={<div>Note 1</div>} />
        <Column
          className="p-row-note"
          footer={
            <>
              1 kg biogenic carbon is equivalent to 44/12 kg of CO<sub>2</sub>
            </>
          }
          colSpan={isISO21930StandardSelected ? 4 : 3}
        />
      </Row>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" />
        <Column className="p-row-note" footer={<div>Note 2</div>} />
        <Column
          className="p-row-note"
          footer={<div>The unit will be adjusted based on the declared or functional unit selected in Step 1.</div>}
          colSpan={4}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTableStyled
      key={gridId}
      header={header}
      footerColumnGroup={footerGroup}
      value={gridData}
      dataKey="ord"
      tableStyle={{ minWidth: '49rem' }}
      showGridlines
      editMode="cell"
      rowClassName={rowClassName}
      {...dataTableProps}
    >
      {checkboxColumnNode}

      <Column
        field="name"
        header="Material name"
        editor={isReadOnly ? undefined : cellTextEditor}
        onCellEditComplete={handleCellEdit}
        body={textCellBody({ placeholder: placeholders.typeName })}
      />

      <Column
        field="weight"
        header={replaceInString('Weight, kg')}
        editor={isReadOnly ? undefined : (opt) => cellNumberEditor(opt, { maxFractionDigits: 8 })}
        onCellEditComplete={handleCellEdit}
        body={numericCellBody({ placeholder: placeholders.typeWeight })}
      />

      <Column
        field="weightPercent"
        header={replaceInString('Weight-% (versus the product)')}
        editor={isReadOnly ? undefined : (opt) => cellNumberEditor(opt, { maxFractionDigits: 8 })}
        onCellEditComplete={handleCellEdit}
        body={numericCellBody({ placeholder: placeholders.typeWeight })}
      />

      <Column
        field="biogenicCarbonWeight"
        header={replaceInNode(getBiogenicCarbonWeightHeader(lcaSpecification?.epdBasedOnUnit))}
        editor={isReadOnly ? undefined : (opt) => cellNumberEditor(opt, { maxFractionDigits: 8, min: 0 })}
        onCellEditComplete={handleCellEdit}
        body={numericCellBody({ placeholder: placeholders.typeKgCPerKg })}
      />

      {isISO21930StandardSelected && (
        <Column
          field="biogenicCO2Weight"
          header={replaceInNode(getBiogenicCO2WeightHeader(lcaSpecification?.epdBasedOnUnit))}
          body={numericCellBody({
            placeholder: (
              <>
                kg CO<sub>2</sub>/kg
              </>
            ),
          })}
        />
      )}
    </DataTableStyled>
  );
};

export default PackagingMaterialsGrid;
